<template>
  <CardLayoutV2 title="Move In Sources">
    <v-skeleton-loader v-if="loading" class="mt-4" type="image, image" />
    <div v-else-if="!hasData">
      <em class="mt-10">No Data Available</em>
    </div>
    <div v-else>
      <div style="height: 400px">
        <StackedBarChart
          :chart-data="chartData"
          :redirect-path="{ name: 'MoveInSourcesReport' }"
        />
      </div>
      <v-row class="mt-3 mx-0">
        <v-col cols="6">
          <v-card outlined class="pa-2 text-center">
            <div class="mb-2">Top Move-In Sources (This month)</div>
            <div
              v-for="src in topSources"
              :key="src.label"
              class="d-flex justify-space-between py-1 font-weight-bold"
            >
              <span class="d-flex align-center">
                <span
                  :style="{
                    backgroundColor: sourceColorMap[src.label],
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    flexShrink: 0,
                    marginRight: '6px',
                  }"
                />
                {{ src.label }}
              </span>
              <span>{{
                currentMonthTotal > 0
                  ? Math.round((src.count / currentMonthTotal) * 100) + "%"
                  : "0%"
              }}</span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card outlined class="pa-2 text-center">
            <div class="mb-2">Least Active Sources (This month)</div>
            <div
              v-for="src in bottomSources"
              :key="src.label"
              class="d-flex justify-space-between py-1 font-weight-bold"
            >
              <span class="d-flex align-center">
                <span
                  :style="{
                    backgroundColor: sourceColorMap[src.label],
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    flexShrink: 0,
                    marginRight: '6px',
                  }"
                />
                {{ src.label }}
              </span>
              <span>{{
                currentMonthTotal > 0
                  ? Math.round((src.count / currentMonthTotal) * 100) + "%"
                  : "0%"
              }}</span>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </CardLayoutV2>
</template>

<script>
import CardLayoutV2 from "@layout/CardLayoutV2.vue";
import StackedBarChart from "./StackedBarChart.vue";
import { computed } from "vue";
import useDashboard from "../useDashboard";
import useUser from "@components/authentication/useUser";
import { getLast12Months, formatMonthYear } from "@tod-ui/helpers/datetime";
import { MONTHLY_MOVE_IN_SOURCES } from "./graphql";
import { CHART_COLORS } from "@tod-ui/constants/colors";

export default {
  name: "CardMoveInSources",
  components: {
    CardLayoutV2,
    StackedBarChart,
  },
  setup() {
    const { defaultOrganization } = useUser();
    const { selectedFacilities } = useDashboard();

    const last12Months = computed(() =>
      getLast12Months(null, defaultOrganization.value.timeZone)
    );

    return {
      selectedFacilities,
      defaultOrganization,
      last12Months,
    };
  },
  data() {
    return {
      moveInSourcesData: [],
    };
  },
  computed: {
    loading() {
      return this.$apollo.queries.moveInSourcesData.loading;
    },
    hasData() {
      return this.moveInSourcesData.length > 0;
    },
    currentMonthDate() {
      return this.last12Months[0];
    },
    allSources() {
      const totals = {};
      this.moveInSourcesData.forEach((d) => {
        totals[d.moveInSource] =
          (totals[d.moveInSource] || 0) + (d.moveInCount || 0);
      });
      return Object.keys(totals).sort((a, b) => totals[a] - totals[b]);
    },
    sourceMonthLookup() {
      const lookup = {};
      this.moveInSourcesData.forEach((d) => {
        const monthLabel = formatMonthYear(
          d.moveInMonth,
          this.defaultOrganization.timeZone
        );
        const key = `${d.moveInSource}__${monthLabel}`;
        lookup[key] = (lookup[key] || 0) + (d.moveInCount || 0);
      });
      return lookup;
    },
    chartData() {
      const months = [...this.last12Months].reverse();
      const labels = months.map((m) =>
        formatMonthYear(m, this.defaultOrganization.timeZone)
      );
      const datasets = this.allSources.map((source, idx) => ({
        label: source,
        data: labels.map(
          (monthLabel) =>
            this.sourceMonthLookup[`${source}__${monthLabel}`] || null
        ),
        backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
      }));
      return { labels, datasets };
    },
    currentMonthSources() {
      const currentLabel = formatMonthYear(
        this.currentMonthDate,
        this.defaultOrganization.timeZone
      );
      return this.allSources
        .map((source) => ({
          label: source,
          count: this.sourceMonthLookup[`${source}__${currentLabel}`] || 0,
        }))
        .sort((a, b) => b.count - a.count);
    },
    sourceColorMap() {
      const map = {};
      this.allSources.forEach((source, idx) => {
        map[source] = CHART_COLORS[idx % CHART_COLORS.length];
      });
      return map;
    },
    currentMonthTotal() {
      return this.currentMonthSources.reduce((sum, src) => sum + src.count, 0);
    },
    topSources() {
      return this.currentMonthSources.slice(0, 3);
    },
    bottomSources() {
      const sources = this.currentMonthSources;
      return sources.slice(Math.max(0, sources.length - 3)).reverse();
    },
  },
  apollo: {
    moveInSourcesData: {
      query: MONTHLY_MOVE_IN_SOURCES,
      variables() {
        return {
          ids: this.selectedFacilities.map((f) => f.id),
          month: this.last12Months,
        };
      },
      update: (data) => data.monthlyMoveInSources,
      skip() {
        return this.selectedFacilities.length === 0;
      },
    },
  },
};
</script>
